import { Card, CardContent } from '@/components/ui/card';
import { Target } from 'lucide-react';
import type { GoalSummaryData } from '@/types/goals';
import { cn } from '@/lib/utils';
import {
  SLIDE_PADDING,
  SLIDE_CARD,
  SLIDE_SCROLL_BOTTOM_PAD,
  STORY_CARD_BG,
  NARRATIVE,
} from './constants';
import { StoryCardHeader } from './StoryCardHeader';
import { Counter } from './StoryCounter';
import { ProgressRing } from './ProgressRing';
import { formatDate } from './utils';

export function CardGoalSummary({
  summary,
  active,
  className,
}: Readonly<{
  summary: GoalSummaryData;
  active: boolean;
  className?: string;
}>) {
  return (
    <Card
      className={cn(SLIDE_CARD, 'relative overflow-hidden', STORY_CARD_BG, className)}
    >
      <CardContent
        className={cn(
          'flex min-h-0 flex-1 flex-col justify-center gap-6 overflow-y-auto overscroll-y-contain sm:gap-8',
          SLIDE_PADDING,
          SLIDE_SCROLL_BOTTOM_PAD
        )}
      >
        <StoryCardHeader
          icon={<Target className="h-5 w-5" />}
          title="สรุปเป้าหมาย"
          subtitle="ความคืบหน้าตลอดระยะเวลาเป้าหมาย"
          narrative={NARRATIVE.goalSummary}
        />

        <div className="relative mx-auto flex items-center justify-center py-2">
          <div className="absolute inset-0 rounded-full bg-primary/5 blur-2xl" />
          <div className="relative">
            <ProgressRing
              percentage={summary.completion_percentage}
              active={active}
              size={152}
              strokeWidth={14}
            />
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-bold tabular-nums text-primary sm:text-4xl">
                <Counter
                  value={summary.completion_percentage}
                  active={active}
                  duration={2000}
                />
                <span className="text-xl font-semibold sm:text-2xl">%</span>
              </span>
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                สำเร็จ
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 min-w-0 sm:gap-4">
          {[
            {
              label: 'Khatm เป้าหมาย',
              node: (
                <>
                  <Counter value={summary.khatm_completed} active={active} />
                  <span className="text-sm font-normal text-muted-foreground">
                    {' '}
                    / {summary.khatm_target}
                  </span>
                </>
              ),
            },
            {
              label: 'หน้ารวม',
              node: (
                <>
                  <Counter
                    value={summary.total_pages_read}
                    active={active}
                  />
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    / {summary.total_pages_target.toLocaleString('th-TH')}
                  </p>
                </>
              ),
            },
            {
              label: 'เริ่มต้น',
              node: (
                <span className="text-sm font-semibold">
                  {formatDate(summary.goal.start_date)}
                </span>
              ),
            },
            {
              label: 'สิ้นสุด',
              node: (
                <span className="text-sm font-semibold">
                  {formatDate(summary.goal.end_date)}
                </span>
              ),
            },
          ].map((item) => (
            <div
              key={item.label}
              className="rounded-2xl border border-primary/20 bg-background/30 p-4 shadow-[0_2px_10px_-4px_hsl(var(--primary)/0.1)] mix-blend-multiply dark:mix-blend-normal"
            >
              <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {item.label}
              </p>
              <p className="text-lg font-bold tabular-nums sm:text-xl">
                {item.node}
              </p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
